import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { db } from "@/api/db";
import { useAuth } from "@/lib/AuthContext";
import {
  LayoutDashboard,
  LogOut, Menu, ChevronRight, ChevronUp, ChevronDown, Settings, Eye, EyeOff, Compass, Map, Mic, Radio, Video, ShoppingBag, UserCircle2, Briefcase, Globe, Users, TrendingUp, GraduationCap, BadgeCheck, FileText, CalendarDays, CalendarCheck, FlaskConical, Lightbulb, Shield
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

export default function Layout({ children, currentPageName }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [previewAsUser, setPreviewAsUser] = useState(() => sessionStorage.getItem("previewAsUser") === "true");
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const intelligencePages = ["MarketMap"];
  const mentorPages       = ["LiveLessons", "Recordings", "EventCalendar", "Workshops", "Tracks", "TrackDetail"];
  const labPages          = ["VocationalTest", "DiscTest", "SaboteurTest", "InterviewSimulator", "MyPortfolio"];
  const oportunidadesPages = ["Opportunities", "MyApplications", "VagasRemotas", "OpportunityDetail"];
  const [intelligenceOpen, setIntelligenceOpen] = useState(() => intelligencePages.includes(currentPageName));
  const [mentorOpen, setMentorOpen]             = useState(() => mentorPages.includes(currentPageName));
  const [labOpen, setLabOpen]                   = useState(() => labPages.includes(currentPageName));
  const [oportunidadesOpen, setOportunidadesOpen] = useState(() => oportunidadesPages.includes(currentPageName));
  const location = useLocation();
  const { logout } = useAuth();

  const [avatarUrl, setAvatarUrl] = useState(null);

  // Load user/profile once on mount (not on every page navigation)
  useEffect(() => {
    db.auth.me().then(u => {
      setUser(u);
      if (u) {
        db.entities.UserProfile.filter({ user_email: u.email }, "-created_date", 1)
          .then(profiles => setProfile(profiles[0] || null));
        db.profile.get().then(p => {
          if (p?.avatarUrl) setAvatarUrl(p.avatarUrl);
        }).catch(() => {});
      }
    }).catch(() => {});
  }, []);

  // Octadesk chat widget
  useEffect(() => {
    if (!user) return;
    const scriptId = "octadesk-chat-script";
    if (document.getElementById(scriptId)) return;
    window.octadesk = window.octadesk || {};
    window.octadesk.chatOptions = {
      subDomain: "o210925-3d3",
      showButton: "true",
      openOnMessage: "true",
      showFooterPoweredBy: "true",
      hide: "false",
    };
    const script = document.createElement("script");
    script.id = scriptId;
    script.async = true;
    script.src = "https://cdn.octadesk.com/embed.js";
    document.body.appendChild(script);
    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, [user]);

  // Listen for avatar updates from ProfileSettings
  useEffect(() => {
    function onAvatarUpdate(e) {
      if (e.detail?.avatarUrl) setAvatarUrl(e.detail.avatarUrl);
    }
    window.addEventListener("avatar-updated", onAvatarUpdate);
    return () => window.removeEventListener("avatar-updated", onAvatarUpdate);
  }, []);

  const hasSeal = user?.subscription_started_at && (new Date() - new Date(user.subscription_started_at)) >= 30 * 24 * 60 * 60 * 1000;
  const isAdmin = user?.role === "admin";
  // When previewing as user, behave as if not admin
  const effectiveIsAdmin = isAdmin && !previewAsUser;

  function togglePreview() {
    const next = !previewAsUser;
    setPreviewAsUser(next);
    sessionStorage.setItem("previewAsUser", String(next));
  }

  const navItems = [
    { label: "Início", icon: LayoutDashboard, page: "Home" },
    {
      label: "Conteúdos", icon: Radio, group: "mentor",
      children: [
        { label: "Calendário", icon: CalendarDays, page: "EventCalendar" },
        { label: "Aulas ao vivo", icon: Radio, page: "LiveLessons" },
        { label: "Oficinas", icon: CalendarCheck, page: "Workshops" },
        { label: "Gravações", icon: Video, page: "Recordings" },
        { label: "Trilhas de carreira", icon: GraduationCap, page: "Tracks" },
      ],
    },
    {
      label: "Intelligence", icon: Lightbulb, group: "intelligence",
      children: [
        { label: "Mapa de Profissões", icon: Map, page: "MarketMap", href: "/MarketMap" },
        { label: "Remunerações", icon: TrendingUp, page: "MarketMap", href: "/MarketMap?view=simulator" },
      ],
    },
    {
      label: "LAB", icon: FlaskConical, group: "lab",
      children: [
        { label: "Vocacional", icon: Compass, page: "VocationalTest" },
        { label: "Comportamental", icon: Users, page: "DiscTest" },
        // { label: "Sabotadores", icon: Shield, page: "SaboteurTest" },
        { label: "Simulador de Entrevista", icon: Mic, page: "InterviewSimulator" },
        { label: "Construtor de Portfólio", icon: UserCircle2, page: "MyPortfolio" },
      ],
    },
    {
      label: "Oportunidades", icon: Globe, group: "oportunidades",
      children: [
        { label: "Vagas Portüs", icon: Briefcase, page: "Opportunities" },
        { label: "Vagas Globais", icon: Globe, page: "VagasRemotas" },
        { label: "Minhas Candidaturas", icon: FileText, page: "MyApplications" },
      ],
    },
    { label: "Meu Perfil", icon: UserCircle2, page: "ProfileSettings" },
  ];

  const adminMarketplacePages = ["AdminMarketplace", "AdminPartnerApplications"];
  const [adminMarketplaceOpen, setAdminMarketplaceOpen] = useState(() => adminMarketplacePages.includes(currentPageName));

  const adminItems = [
    { label: "Usuários", icon: Users, page: "AdminUsers" },
    { label: "Gerenciar Trilhas", icon: Settings, page: "AdminTracks" },
    { label: "Vagas Remotas", icon: Briefcase, page: "AdminVagasRemotas" },
    { label: "Encontros", icon: Radio, page: "AdminLiveLessons" },
    {
      label: "Marketplace", icon: ShoppingBag, group: "adminMarketplace",
      children: [
        { label: "Cursos", icon: ShoppingBag, page: "AdminMarketplace" },
        { label: "Aplicações Parceiros", icon: Users, page: "AdminPartnerApplications" },
      ],
    },
  ];

  const isActive = (page) => {
    if (page === "Tracks" && currentPageName === "TrackDetail") return true;
    return currentPageName === page;
  };

  function isChildActive(child) {
    if (!child.href || child.page !== "MarketMap" || currentPageName !== "MarketMap") {
      return isActive(child.page);
    }
    const viewParam = new URLSearchParams(location.search).get("view");
    if (child.href.includes("view=simulator")) return viewParam === "simulator";
    if (child.href.includes("view=comparison")) return viewParam === "comparison";
    return !viewParam;
  }

  const noLayoutPages = ["Onboarding", "PublicPortfolio", "MyPortfolio", "AplicacaoParceiros"];
  if (noLayoutPages.includes(currentPageName)) {
    return <>{children}</>;
  }

  // Expose previewAsUser to children via a global so pages can read it
  window.__portusPreviewAsUser = previewAsUser;

  return (
    <div className="min-h-screen flex" style={{ background: '#ffffff' }}>
      {/* Glassmorphism blob — fixed in viewport bottom-left */}
      <div style={{
        position: 'fixed',
        bottom: -150,
        left: -150,
        width: 860,
        height: 860,
        background: 'radial-gradient(circle, rgba(0,220,164,0.15) 0%, transparent 62%)',
        pointerEvents: 'none',
        zIndex: 0,
      }} />

      <style>{`
        :root {
          --portus-green: #00DCA4;
          --portus-green-dark: #00664C;
          --portus-green-light: #B0FCEA;
        }
        .sidebar-nav-active {
          background-color: #EAFAF6;
          color: #00664C;
        }
        .sidebar-nav-active svg {
          color: #008572;
        }
        /* Glassmorphism: aplica efeito de vidro em todos os cards/painéis brancos dentro do conteúdo */
        main .bg-white {
          background-color: rgba(255, 255, 255, 0.72) !important;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
        }
      `}</style>

      {/* Preview mode banner */}
      {previewAsUser && (
        <div className="fixed top-0 left-0 right-0 z-[100] flex items-center justify-between px-4 py-2 text-sm font-semibold" style={{ background: '#E5FF52', color: '#003326' }}>
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            Modo visualização — você está vendo como um usuário comum
          </div>
          <button onClick={togglePreview} className="flex items-center gap-1.5 underline text-xs font-semibold">
            <EyeOff className="w-3.5 h-3.5" /> Sair do modo
          </button>
        </div>
      )}

      {/* Sidebar */}
      <aside className={`fixed ${previewAsUser ? "top-10" : "top-0"} inset-y-0 left-0 z-50 w-64 bg-white shadow-sm transform transition-transform duration-200 lg:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"} flex flex-col`}
        style={{ borderRight: '1px solid #E5E9EA' }}>
        {/* Logo */}
        <div className="flex items-center justify-center px-4 h-16 flex-shrink-0" style={{ borderBottom: '1px solid #E5E9EA' }}>
          <img src="/logo.png" alt="Portüs Talent Pro" style={{ height: 44, width: "auto" }} />
        </div>

        {/* Nav */}
        <nav className="px-3 py-4 flex flex-col gap-1 flex-1 min-h-0 overflow-y-auto">
          {navItems.map(item => {
            // ── Group item (submenu) ──
            if (item.children) {
              const anyChildActive = item.children.some(c => isActive(c.page));
              const open =
                item.group === "intelligence"   ? intelligenceOpen :
                item.group === "mentor"         ? mentorOpen :
                item.group === "lab"            ? labOpen :
                item.group === "oportunidades"  ? oportunidadesOpen : false;
              const toggleOpen =
                item.group === "intelligence"   ? () => setIntelligenceOpen(o => !o) :
                item.group === "mentor"         ? () => setMentorOpen(o => !o) :
                item.group === "lab"            ? () => setLabOpen(o => !o) :
                item.group === "oportunidades"  ? () => setOportunidadesOpen(o => !o) : undefined;
              return (
                <div key={item.group}>
                  <button
                    onClick={toggleOpen}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-full text-base font-medium transition-colors w-full hover:bg-[#F2F4F4]"
                    style={{ color: anyChildActive ? '#00664C' : '#646C6F', background: anyChildActive && !open ? '#EAFAF6' : 'transparent' }}
                  >
                    <item.icon className="w-4 h-4" style={{ color: anyChildActive ? '#008572' : '#646C6F' }} />
                    <span className="flex-1 text-left">{item.label}</span>
                    <ChevronDown className="w-3.5 h-3.5" style={{ color: '#C8CED0', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
                  </button>
                  {open && (
                    <div className="mt-0.5 ml-3 pl-3 flex flex-col gap-0.5" style={{ borderLeft: '2px solid #E5E9EA' }}>
                      {item.children.map(child => {
                        const active = isChildActive(child);
                        return (
                          <Link
                            key={child.href || child.page}
                            to={child.href || createPageUrl(child.page)}
                            onClick={() => setMobileOpen(false)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap overflow-hidden ${
                              active ? "sidebar-nav-active" : "hover:bg-[#F2F4F4]"
                            }`}
                            style={active ? {} : { color: '#646C6F' }}
                          >
                            <child.icon className="w-4 h-4 flex-shrink-0" style={active ? { color: '#008572' } : {}} />
                            <span className="truncate">{child.label}</span>
                            {child.badge && <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold leading-none flex-shrink-0" style={{ background: "#FEEDB4", color: "#65541B" }}>{child.badge}</span>}
                            {active && <ChevronRight className="w-3 h-3 ml-auto flex-shrink-0" style={{ color: '#00DCA4' }} />}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // ── Regular item ──
            return (
              <Link
                key={item.page}
                to={createPageUrl(item.page)}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-full text-base font-medium transition-colors ${
                    isActive(item.page)
                      ? "sidebar-nav-active"
                      : "hover:bg-[#F2F4F4]"
                  }`}
                style={isActive(item.page) ? {} : {color: '#646C6F'}}
                >
                  <item.icon className="w-4 h-4" style={isActive(item.page) ? {color: '#008572'} : {}} />
                {item.label}
                {isActive(item.page) && <ChevronRight className="w-3 h-3 ml-auto" style={{color: '#00DCA4'}} />}
              </Link>
            );
          })}

          {effectiveIsAdmin && (
            <>
              <div className="mt-4 mb-1 px-3">
                <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#99A6AB' }}>Admin</p>
              </div>
              {adminItems.map(item => {
                if (item.children) {
                  const anyChildActive = item.children.some(c => isActive(c.page));
                  const open = item.group === "adminMarketplace" ? adminMarketplaceOpen : false;
                  const toggleOpen = item.group === "adminMarketplace" ? () => setAdminMarketplaceOpen(o => !o) : undefined;
                  return (
                    <div key={item.group}>
                      <button
                        onClick={toggleOpen}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-full text-sm font-medium transition-colors w-full hover:bg-[#F2F4F4]"
                        style={{ color: anyChildActive ? '#00664C' : '#646C6F', background: anyChildActive && !open ? '#EAFAF6' : 'transparent' }}
                      >
                        <item.icon className="w-4 h-4" style={{ color: anyChildActive ? '#008572' : '#646C6F' }} />
                        <span className="flex-1 text-left">{item.label}</span>
                        <ChevronDown className="w-3.5 h-3.5" style={{ color: '#C8CED0', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
                      </button>
                      {open && (
                        <div className="mt-0.5 ml-3 pl-3 flex flex-col gap-0.5" style={{ borderLeft: '2px solid #E5E9EA' }}>
                          {item.children.map(child => (
                            <Link
                              key={child.page}
                              to={createPageUrl(child.page)}
                              onClick={() => setMobileOpen(false)}
                              className={`flex items-center gap-3 px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                                isActive(child.page) ? "sidebar-nav-active" : "hover:bg-[#F2F4F4]"
                              }`}
                              style={isActive(child.page) ? {} : { color: '#646C6F' }}
                            >
                              <child.icon className="w-4 h-4" style={isActive(child.page) ? { color: '#008572' } : {}} />
                              {child.label}
                              {isActive(child.page) && <ChevronRight className="w-3 h-3 ml-auto" style={{ color: '#00DCA4' }} />}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <Link
                    key={item.page}
                    to={createPageUrl(item.page)}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-full text-sm font-medium transition-colors ${
                        isActive(item.page)
                          ? "sidebar-nav-active"
                          : "hover:bg-[#F2F4F4]"
                      }`}
                    style={isActive(item.page) ? {} : {color: '#646C6F'}}
                    >
                      <item.icon className="w-4 h-4" style={isActive(item.page) ? {color: '#008572'} : {}} />
                    {item.label}
                  </Link>
                );
              })}
            </>
          )}
        </nav>

        {/* Bottom — user menu */}
        {user && (
          <div className="flex-shrink-0 px-3 pb-3" style={{ borderTop: '1px solid #E5E9EA' }}>

            {/* Submenu (abre para cima) */}
            {userMenuOpen && (
              <div className="mb-2 py-1.5 rounded-2xl space-y-0.5" style={{ background: '#fff', border: '1px solid #E5E9EA', boxShadow: '0 -4px 16px 0 rgba(0,0,0,0.06)' }}>
                {isAdmin && (
                  <button
                    onClick={togglePreview}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-full text-sm font-medium w-full transition-colors"
                    style={previewAsUser
                      ? { background: '#E5FF52', color: '#003326' }
                      : { color: '#646C6F' }}
                    onMouseEnter={e => { if (!previewAsUser) e.currentTarget.style.background = '#F2F4F4'; }}
                    onMouseLeave={e => { if (!previewAsUser) e.currentTarget.style.background = 'transparent'; }}
                  >
                    {previewAsUser ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    {previewAsUser ? "Sair do modo usuário" : "Ver como usuário"}
                  </button>
                )}
                <button
                  onClick={logout}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-full text-sm font-medium w-full transition-colors"
                  style={{ color: '#646C6F' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F2F4F4'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <LogOut className="w-4 h-4" />
                  Sair
                </button>
              </div>
            )}

            {/* User row — clicável */}
            <button
              onClick={() => setUserMenuOpen(o => !o)}
              className="flex items-center gap-3 w-full px-3 py-2.5 rounded-full transition-colors"
              onMouseEnter={e => e.currentTarget.style.background = '#F2F4F4'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <Avatar className="w-8 h-8 flex-shrink-0">
                {avatarUrl && <AvatarImage src={avatarUrl} alt={user.full_name || 'Avatar'} />}
                <AvatarFallback className="text-sm font-semibold" style={{ background: '#B0FCEA', color: '#00664C' }}>
                  {user.full_name?.charAt(0) || user.email?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <span className="flex-1 min-w-0 text-left text-sm font-semibold truncate" style={{ color: '#2E3233' }}>
                {user.full_name || 'Usuário'}
              </span>
              {hasSeal && (
                <BadgeCheck className="w-4 h-4 flex-shrink-0" style={{ color: '#00DCA4' }} title="Selo Talent Pro" />
              )}
              {profile?.is_subscriber && !previewAsUser && (
                <Badge className="text-xs px-1.5 py-0.5 font-semibold border-0 flex-shrink-0" style={{ background: '#00DCA4', color: '#003326' }}>Pro</Badge>
              )}
              {userMenuOpen
                ? <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: '#99A6AB' }} />
                : <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: '#99A6AB' }} />
              }
            </button>
          </div>
        )}
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/30 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Main */}
      <div className={`flex-1 lg:ml-64 flex flex-col min-h-screen ${previewAsUser ? "pt-10" : ""}`}>
        {/* Mobile topbar */}
        <header className="lg:hidden sticky top-0 z-30 bg-white h-14 flex items-center px-4 gap-3"
          style={{ borderBottom: '1px solid #E5E9EA' }}>
          <button onClick={() => setMobileOpen(true)} className="p-1.5 rounded-full"
            style={{ color: '#646C6F' }}
            onMouseEnter={e => e.currentTarget.style.background = '#F2F4F4'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <Menu className="w-5 h-5" style={{ color: '#646C6F' }} />
          </button>
          <img src="/logo.png" alt="Portüs Talent Pro" style={{ height: 24, width: "auto" }} />
        </header>

        <main className="flex-1 p-4 lg:p-8" style={{ backgroundColor: 'transparent' }}>
          <div style={{ position: 'relative', zIndex: 1 }}>
          {children}
          </div>
        </main>
      </div>
    </div>
  );
}